import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  SimpleGrid,
  Card,
  CardBody,
  Avatar,
  Radio,
  RadioGroup,

  useColorModeValue,
  Heading,
} from '@chakra-ui/react';

export default function AgentSelectionInline({ 
  agents, 
  platformName, 
  onNext, 
  onCancel,
  isLoading = false 
}) {
  const [selectedAgentId, setSelectedAgentId] = useState('');
  
  const cardBg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.200', 'navy.600');
  const hoverBg = useColorModeValue('gray.50', 'navy.700');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'secondaryGray.300');

  const handleNext = () => {
    const selectedAgent = agents.find(agent => agent.id === selectedAgentId);
    if (selectedAgent && onNext) {
      onNext(selectedAgent);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Box maxW="800px" mx="auto" p={6}>
      <VStack spacing={8} align="stretch">
        {/* Header */}
        <VStack spacing={2} textAlign="center">
          <Heading size="lg" color={textColor}>
            Connect {platformName} to Agent
          </Heading>
          <Text color={textColorSecondary} fontSize="md">
            Select which agent should have access to this {platformName} integration
          </Text>
        </VStack>

        {/* Agent Selection */}
        <Box>
          <RadioGroup value={selectedAgentId} onChange={setSelectedAgentId}>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
              {agents.map((agent) => (
                <Card
                  key={agent.id}
                  bg={cardBg}
                  border="2px solid"
                  borderColor={selectedAgentId === agent.id ? 'brand.500' : borderColor}
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{
                    bg: hoverBg,
                    borderColor: selectedAgentId === agent.id ? 'brand.500' : 'brand.300',
                    transform: 'translateY(-2px)',
                    shadow: 'lg'
                  }}
                  onClick={() => setSelectedAgentId(agent.id)}
                >
                  <CardBody p={4}>
                    <VStack spacing={3}>
                      <Avatar
                        size="lg"
                        name={agent.name}
                        src={agent.avatar}
                        bg="brand.500"
                        color="white"
                      />
                      <VStack spacing={1} textAlign="center">
                        <Text fontWeight="600" color={textColor} fontSize="md">
                          {agent.name}
                        </Text>
                        {agent.description && (
                          <Text 
                            color={textColorSecondary} 
                            fontSize="sm" 
                            noOfLines={2}
                            textAlign="center"
                          >
                            {agent.description}
                          </Text>
                        )}
                      </VStack>
                      <Radio 
                        value={agent.id} 
                        colorScheme="brand"
                        size="lg"
                      />
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </RadioGroup>
        </Box>

        {/* Action Buttons */}
        <HStack spacing={4} justify="center" pt={4}>
          <Button
            variant="outline"
            colorScheme="gray"
            size="lg"
            onClick={handleCancel}
            isDisabled={isLoading}
            minW="120px"
          >
            Cancel
          </Button>
          <Button
            colorScheme="brand"
            size="lg"
            onClick={handleNext}
            isDisabled={!selectedAgentId || isLoading}
            isLoading={isLoading}
            loadingText="Connecting..."
            minW="120px"
          >
            Next
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
